import { createStytchHeadlessClient } from '@stytch/nextjs/headless';
import { AuthError, AuthErrorCodes } from './error';
const hasURLSearchParams = typeof window !== 'undefined' && typeof URLSearchParams !== 'undefined';
/**
 * The `StytchClient` is a Chord compatabile wrapper around the following Stytch
 * libraries.
 *
 * - [@stytch/nextjs](https://www.npmjs.com/package/@stytch/nextjs)
 * - [@stytch/vanilla-js](https://www.npmjs.com/package/@stytch/vanilla-js)
 *
 * @example
 *
 * ```javascript
 * import StytchClient from '@chordcommerce/chord-stytch'
 * const stytchClient = new StytchClient({
 *   apiKey: process.env.STYTCH_API_KEY,
 *   redirectPath: '/authenticate',
 *   sessionDurationMinutes: 60,
 * })
 * ```
 */
class StytchClient {
  client;
  redirectPath;
  sessionDurationMinutes;
  constructor(options) {
    if (!options.apiKey) {
      throw new AuthError({
        message: 'An API key is required',
        code: AuthErrorCodes.MissingApiKey
      });
    }
    this.redirectPath = options.redirectPath ?? '/authenticate';
    this.sessionDurationMinutes = options.sessionDurationMinutes ?? 60;
    if (typeof window === 'undefined') return;
    this.client = createStytchHeadlessClient(options.apiKey);
  }
  async pollLoginStatus() {
    return new Promise(resolve => {
      const intervalID = setInterval(async () => {
        const user = await this.getUser();
        if (user) {
          clearInterval(intervalID);
          resolve(user);
        }
      }, 1000);
    });
  }
  async getToken() {
    let tokens = null;
    try {
      tokens = this.client.session.getTokens();
    } catch (error) {
      throw new AuthError({
        message: error.message,
        code: AuthErrorCodes.InternalError
      });
    }
    if (!tokens) {
      throw new AuthError({
        message: 'User is not logged in',
        code: AuthErrorCodes.Unauthorized
      });
    }
    return tokens?.session_token;
  }
  getUser() {
    return this.client?.user.get();
  }
  isLoggedIn() {
    return this.client?.session.getSync() !== null;
  }
  async login(_ref) {
    let {
      email
    } = _ref;
    await this.client?.magicLinks.email.loginOrCreate(email, {
      login_magic_link_url: window.location.origin + this.redirectPath,
      signup_magic_link_url: window.location.origin + this.redirectPath
    });
    // In order to satisfy the login method type contract of other Chord auth
    // packages, we must not return the login method until the user has logged
    // in. This happens when handleCallback is called in another tab by clicking
    // on the magic link.
    return this.pollLoginStatus();
  }
  logout() {
    return this.client.session.revoke();
  }
  async handleCallback() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const type = params.get('stytch_token_type');
    if (!token || !type) {
      throw new AuthError({
        message: 'In order to handleCallback, token and stytch_token_type must be in the query parameters',
        code: AuthErrorCodes.Unauthorized
      });
    }
    if (type === 'oauth') {
      const resp = await this.client.oauth.authenticate(token, {
        session_duration_minutes: this.sessionDurationMinutes
      });
      return resp.session_token;
    }
    const resp = await this.client.magicLinks.authenticate(token, {
      session_duration_minutes: this.sessionDurationMinutes
    });
    return resp.session_token;
  }
  getServiceClient() {
    return this.client;
  }
  currentUrlHandlesCallback() {
    if (!hasURLSearchParams) {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const type = params.get('stytch_token_type');
    return token && type && window.location.pathname === this.redirectPath;
  }
}
export default StytchClient;